import { useEffect } from "react";

/**
 * Lightweight SPA SEO hook — sets document.title, meta description, and canonical link
 * per route. No new dependencies. Googlebot executes JS so this is indexable.
 *
 * Usage:
 *   usePageMeta({
 *     title: "Pricing — ColourMatch",
 *     description: "Try ColourMatch for free, then unlock the full project for $29.99.",
 *     path: "/pricing",
 *   });
 */
const SITE = "https://colourmatch.au";
const DEFAULT_TITLE = "ColourMatch — AI Paint Visualiser for Australian Homes";
const DEFAULT_DESC = "ColourMatch is an AI paint visualiser (software, not a painting company). Upload your own photo of your home and preview hundreds of interior and exterior paint colours in seconds. Built for Australian homeowners.";

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function usePageMeta({ title, description, path, ogImage, robots, jsonLd } = {}) {
  useEffect(() => {
    const fullTitle = title || DEFAULT_TITLE;
    const desc = description || DEFAULT_DESC;
    const url = path ? `${SITE}${path}` : SITE;
    document.title = fullTitle;
    setMeta("description", desc);
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:url", url, "property");
    setMeta("og:type", "website", "property");
    if (ogImage) setMeta("og:image", ogImage, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", desc);
    if (ogImage) setMeta("twitter:image", ogImage);
    setCanonical(url);
    // Optional robots directive (e.g. "noindex,follow" for Block D pre-launch profiles)
    if (robots) {
      setMeta("robots", robots);
    } else {
      const el = document.head.querySelector('meta[name="robots"]');
      if (el) el.remove();
    }
    // Block G — JSON-LD structured data. Identified by data-cm-jsonld so the
    // unmount cleanup only removes ColourMatch-injected scripts.
    document.head.querySelectorAll('script[data-cm-jsonld]').forEach((n) => n.remove());
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((obj) => {
        if (!obj) return;
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-cm-jsonld", "1");
        s.textContent = JSON.stringify(obj);
        document.head.appendChild(s);
      });
    }
    return () => {
      // restore defaults on unmount so the next page renders without stale tags
      // (individual pages set their own immediately on mount)
      document.title = DEFAULT_TITLE;
      const el = document.head.querySelector('meta[name="robots"]');
      if (el) el.remove();
      document.head.querySelectorAll('script[data-cm-jsonld]').forEach((n) => n.remove());
    };
  }, [title, description, path, ogImage, robots, jsonLd]);
}
